import { allMovies } from '@/mocks/movies';
import SectionHeading from './SectionHeading';

const inTheater = allMovies.filter((movie) => movie.cinemaStatus === '院线在映');
const comingSoon = allMovies.filter((movie) => movie.cinemaStatus === '即将同步');

type SyncGroupProps = {
  title: string;
  icon: string;
  accent: string;
  items: typeof allMovies;
  note: string;
};

function SyncGroup({ title, icon, accent, items, note }: SyncGroupProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg text-lg ${accent}`}>
          <i className={icon} aria-hidden="true"></i>
        </span>
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">{title}</h3>
          <p className="text-xs text-foreground-600">共 {items.length} 部</p>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-foreground-700">{note}</p>
      <ul className="mt-5 space-y-3">
        {items.map((movie) => (
          <li
            key={movie.id}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
          >
            <span className="hidden h-16 w-11 shrink-0 overflow-hidden rounded-md sm:block">
              <img src={movie.poster} alt={`${movie.title} 海报`} className="h-full w-full object-cover object-top" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-heading text-sm font-bold text-foreground-950">{movie.title}</span>
                <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-700">
                  {movie.region}
                </span>
              </span>
              <span className="mt-1 text-xs text-foreground-600">
                {movie.genres.join(' · ')} · {movie.runtime} · 导演 {movie.director}
              </span>
              <span className="mt-1 flex items-center gap-1 text-xs text-foreground-600">
                <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
                {movie.cinemaStatus === '院线在映' ? `${movie.releaseDate} 起在映` : '待同步，具体时间以页面标注为准'}
              </span>
            </span>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center">
              <i className="ri-arrow-right-s-line text-xl text-foreground-400" aria-hidden="true"></i>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TheaterSync() {
  return (
    <section id="theater" className="w-full bg-background-50 px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="院线同步"
          title="院线同步：分清“现在能看”和“还要再等”"
          desc="很多人在查询时容易把上映状态弄混。这里把作品分成「院线在映」与「即将同步」两组：前者影院已可购票，后者代表网络端还在等待，帮助你快速判断该现在出门还是先加个收藏。"
        />

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SyncGroup
            title="院线在映"
            icon="ri-building-4-line"
            accent="bg-primary-100 text-primary-700"
            items={inTheater}
            note="以下作品当前处于影院排片阶段，可按上映日期选择想看的新片。部分影片会随热度加开场次。"
          />
          <SyncGroup
            title="即将同步"
            icon="ri-timer-flash-line"
            accent="bg-accent-100 text-accent-800"
            items={comingSoon}
            note="以下作品尚未完成网络同步，标注日期为预估或待定。建议先了解类型与主创，再决定是否等待。"
          />
        </div>

        <div className="mt-6 flex flex-col items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-5 sm:flex-row sm:items-center">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
            <i className="ri-information-line text-xl" aria-hidden="true"></i>
          </span>
          <p className="text-sm leading-relaxed text-secondary-900">
            <strong className="font-medium">温馨提示：</strong>
            院线排片与上线计划可能随时调整，页面标注仅用于帮助你缩小查询范围。出发观影前，建议再确认一次实际场次与时间。
          </p>
        </div>
      </div>
    </section>
  );
}